import React from 'react';

// Shared check-in satisfaction helpers + hover panels, used by the CRM Dashboard
// "Client Check Ins" card and the Client Satisfaction report page.

export const normEmail = (e) => String(e || '').trim().toLowerCase();

export const pickProfilePicUrl = (data) => {
  if (!data) return '';
  const candidates = [
    data.profileImage,
    data.profilePicture,
    data.photoURL,
    data.photoUrl,
    data.avatarUrl,
    data.avatarURL,
    data.imageUrl,
    data.imageURL,
    data.picture,
    data.profilePic,
  ];
  const url = candidates.find((v) => typeof v === 'string' && v.trim().length > 0);
  return url ? url.trim() : '';
};

export function accountDisplayForCheckIn(account, fallbackEmail) {
  const c = account?.contact || {};
  const contactName =
    c.name ||
    [c.firstName, c.lastName].filter(Boolean).join(' ').trim() ||
    '';
  const name =
    account?.company ||
    contactName ||
    fallbackEmail;
  const imageUrl =
    (typeof c.photoURL === 'string' && c.photoURL.trim()) ||
    (typeof account?.photoURL === 'string' && account.photoURL.trim()) ||
    pickProfilePicUrl(account) ||
    '';
  return { name, contactName, imageUrl, accountId: account?.id || null };
}

// Normalize a check-in date (Firestore Timestamp | Date | string) to a JS Date, or null.
export function checkInDateToJs(value) {
  if (!value) return null;
  try {
    const d = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

// Short "Jun 14" style label for the hover tooltip.
export function formatCheckInShortDate(value) {
  const d = checkInDateToJs(value);
  return d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
}

export function initialsFromName(name) {
  return (
    String(name || '?')
      .split(/\s+/)
      .map((w) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?'
  );
}

// Spell out the star level for the hover tooltip (e.g. "nine five star ratings").
export const CHECK_IN_STAR_WORDS = {
  1: 'one',
  2: 'two',
  3: 'three',
  4: 'four',
  5: 'five',
};

// X-axis label for the check-in bar chart: the star number on top, the matching ⭐ emojis below.
export function CheckInStarTick({ x, y, payload, darkMode }) {
  const n = Math.max(0, Math.min(5, Number(payload?.value) || 0));
  const color = darkMode ? '#9ca3af' : '#6b7280';
  return (
    <text x={x} y={y} textAnchor="middle" fill={color}>
      <tspan x={x} dy={16} fontSize={14} fontWeight="600">{payload?.value}</tspan>
      <tspan x={x} dy={17} fontSize={13}>{'⭐'.repeat(n)}</tspan>
    </text>
  );
}

// Hover panel for the check-in bars: shows who gave this rating, their comment, and call requests.
export function CheckInRatingPanel({ row, darkMode }) {
  if (!row) return null;
  const submitters = row.submitters || [];
  const count = row.count ?? 0;
  return (
    <div
      className={`rounded-xl border px-5 py-4 shadow-xl max-w-[420px] ${
        darkMode ? 'border-gray-600 bg-gray-800 text-gray-100' : 'border-gray-200 bg-white text-gray-900'
      }`}
    >
      <div className="flex items-center justify-center gap-2 text-lg font-semibold mb-3">
        <span className="text-2xl leading-none" role="img" aria-label={`${row.stars} star rating`}>⭐</span>
        <span>{count} {CHECK_IN_STAR_WORDS[row.stars] || row.stars} star rating{count !== 1 ? 's' : ''}</span>
      </div>
      {submitters.length === 0 ? (
        <p className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Client names will appear when the check-in includes a known account email.
        </p>
      ) : (
        <ul className="space-y-4 max-h-52 overflow-y-auto pr-1">
          {submitters.map((s, i) => (
            <li key={`${s.accountId || s.name}-${i}`} className="flex items-start gap-3 min-w-0">
              {s.imageUrl ? (
                <img src={s.imageUrl} alt="" className="h-12 w-12 rounded-full object-cover shrink-0 border border-gray-200 dark:border-gray-600 mt-0.5" />
              ) : (
                <div className="h-12 w-12 rounded-full shrink-0 mt-0.5 flex items-center justify-center text-sm font-semibold bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200">
                  {initialsFromName(s.name)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-base font-semibold truncate flex-1 min-w-0">{s.name}</span>
                  {s.comment ? (
                    <span role="img" aria-label="Left a comment" className="text-lg leading-none shrink-0">💬</span>
                  ) : null}
                  {s.requestedCall ? (
                    <span role="img" aria-label="Requested a check-in call" className="text-lg leading-none shrink-0">📞</span>
                  ) : null}
                </div>
                {(() => {
                  const contact = s.contactName && s.contactName !== s.name ? s.contactName : '';
                  const when = formatCheckInShortDate(s.date);
                  if (!contact && !when) return null;
                  return (
                    <div className={`text-sm mt-0.5 truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {contact}{contact && when ? ' · ' : ''}{when}
                    </div>
                  );
                })()}
                {s.comment ? (
                  <p className={`text-sm italic mt-1 break-words whitespace-pre-wrap ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    "{s.comment}"
                  </p>
                ) : null}
                {s.requestedCall ? (
                  <p className={`text-sm mt-1 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                    Requested a check-in call
                  </p>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Hover panel for the Pending summary card: lists clients whose check-in is still outstanding.
export function CheckInPendingPanel({ submitters, darkMode }) {
  const list = submitters || [];
  return (
    <div
      className={`rounded-xl border px-5 py-4 shadow-xl max-w-[420px] ${
        darkMode ? 'border-gray-600 bg-gray-800 text-gray-100' : 'border-gray-200 bg-white text-gray-900'
      }`}
    >
      <div className="flex items-center justify-center gap-2 text-lg font-semibold mb-3">
        <span className="text-2xl leading-none" role="img" aria-label="Pending">⏳</span>
        <span>{list.length} pending check-in{list.length !== 1 ? 's' : ''}</span>
      </div>
      {list.length === 0 ? (
        <p className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No pending check-ins for this month.
        </p>
      ) : (
        <ul className="space-y-4 max-h-52 overflow-y-auto pr-1">
          {list.map((s, i) => {
            const contact = s.contactName && s.contactName !== s.name ? s.contactName : '';
            const when = formatCheckInShortDate(s.date);
            return (
              <li key={`${s.accountId || s.name}-${i}`} className="flex items-start gap-3 min-w-0">
                {s.imageUrl ? (
                  <img src={s.imageUrl} alt="" className="h-12 w-12 rounded-full object-cover shrink-0 border border-gray-200 dark:border-gray-600 mt-0.5" />
                ) : (
                  <div className="h-12 w-12 rounded-full shrink-0 mt-0.5 flex items-center justify-center text-sm font-semibold bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200">
                    {initialsFromName(s.name)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-base font-semibold truncate">{s.name}</div>
                  {(contact || when) ? (
                    <div className={`text-sm mt-0.5 truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {contact}{contact && when ? ' · ' : ''}{when ? `Due ${when}` : ''}
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
